import {DateTime} from 'luxon';
import React, {FC, ReactNode, useEffect, useState} from 'react';
import styled from 'styled-components';

import {
  TimeFormatsEnum,
  withTimezone,
} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SmartDateTimeFormatter} from '../../../../libs/shared/ui/src/components/datetime/smartDateTimeFormatter';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  createFormFieldHoverStyle,
  createFormInputFieldHeight,
} from '../../accounts/createAccount/createAccountStyles';
import {DateTimePicker} from '../dateTime/dateTimePicker';
import {CreateFormInputDisplay} from './createFormInputDisplay';
import {addCreateFormInputDisabledStyles} from './createFormInputHelpers';

/*
 * Props.
 */

interface DateTimePopoverProps {
  title: ReactNode;
  value: string;
  onSelect: (arg: string) => void;
  onDelete?: () => void;
  timeFormat: TimeFormatsEnum;
  isModalInitiallyOpen?: boolean;
  shouldShowClearInDropdown?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  customFormat?: Intl.DateTimeFormatOptions;
  shouldAllowNullDate?: boolean;
  minDateTime?: DateTime;
  isTimeFieldReadOnly?: boolean;
  shouldSetUndefinedDefaultDateTime?: boolean;
  shouldShowTimeZone?: boolean;
  timezone?: string;
  noMargin?: boolean;
}

/*
 * Style.
 */

interface StyledButtonDivProps {
  $isDisabled?: boolean;
}

const StyledButtonDiv = styled.div<StyledButtonDivProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 5px 8px;
  height: ${createFormInputFieldHeight}px;
  width: fit-content;

  ${createFormFieldHoverStyle};
  ${(p) => addCreateFormInputDisabledStyles(p.$isDisabled)}
`;

const StyledPlaceholderDiv = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledPlaceholderTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
  white-space: nowrap;
`;

const StyledDateTimeTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade90};
  white-space: nowrap;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  padding: 0 12px 12px 12px;
`;

/*
 * Component.
 */

export const DateTimePopover: FC<DateTimePopoverProps> = (props) => {
  const theme = useUserTheme();
  const {
    title,
    value,
    shouldShowClearInDropdown,
    onSelect,
    onDelete,
    timeFormat,
    isModalInitiallyOpen,
    isDisabled,
    placeholder,
    customFormat,
    shouldAllowNullDate,
    minDateTime,
    isTimeFieldReadOnly,
    shouldSetUndefinedDefaultDateTime,
    shouldShowTimeZone,
    timezone,
    noMargin,
  } = props;

  useEffect(() => {
    if (shouldSetUndefinedDefaultDateTime) {
      setSelectedDateTime(undefined);
    }
  }, [shouldSetUndefinedDefaultDateTime]);

  const getInitialSelectedDateTime = () => {
    if (value) {
      return DateTime.fromMillis(Number(value), {zone: timezone});
    }

    return DateTime.local({zone: timezone});
  };

  const [selectedDateTime, setSelectedDateTime] = useState<DateTime | undefined>(
    getInitialSelectedDateTime(),
  );

  return (
    <CreateFormInputDisplay
      title={title}
      input={
        <PopoverCoordinator
          isDisabled={isDisabled}
          renderButton={() => (
            <StyledButtonDiv $isDisabled={isDisabled}>
              <StyledButtonContentDiv>
                <Icon name="calendarSmall" color={theme.greys.shade60} size={16} />
                {renderDateTimeOrPlaceholderContent(
                  value,
                  placeholder,
                  customFormat,
                  shouldShowTimeZone,
                  timezone,
                )}
              </StyledButtonContentDiv>
              <Icon name="chevronDownSmall" color={theme.greys.shade60} />
            </StyledButtonDiv>
          )}
          renderModal={(popoverProps) => (
            <Dropdown {...popoverProps}>
              <DateTimePicker
                selectedDateTime={selectedDateTime}
                timeFormat={timeFormat}
                onDateTimeSelect={setSelectedDateTime}
                minDateTime={withTimezone(minDateTime, timezone)}
                isTimeFieldReadOnly={isTimeFieldReadOnly}
                timezone={timezone}
              />
              {maybeRenderActions(
                Boolean(shouldShowClearInDropdown && value && onDelete),
                () => {
                  if (selectedDateTime || shouldAllowNullDate) {
                    onSelect(selectedDateTime?.toMillis().toString() || '');
                    popoverProps.onRequestClose();
                  }
                },
                popoverProps.onRequestClose,
                () => {
                  if (onDelete) {
                    onDelete();
                    popoverProps.onRequestClose();
                  }
                },
              )}
            </Dropdown>
          )}
          isModalInitiallyOpen={Boolean(isModalInitiallyOpen)}
        />
      }
      onDelete={shouldShowClearInDropdown ? undefined : onDelete}
      noMargin={noMargin}
    />
  );
};

/*
 * Helpers.
 */

function maybeRenderActions(
  shouldShowClear?: boolean,
  onDone?: () => void,
  onCancel?: () => void,
  onClear?: () => void,
) {
  return (
    <StyledButtonGroup spacingSize={VisualSizesEnum.SMALL}>
      {shouldShowClear && (
        <Button variant={ButtonVariantsEnum.SECONDARY} size={VisualSizesEnum.SMALL} onClick={onClear}>
          <Trans id="create-form-date-time-popover-clear">Clear</Trans>
        </Button>
      )}
      <ButtonGroupSpacer />
      <Button variant={ButtonVariantsEnum.SECONDARY} size={VisualSizesEnum.SMALL} onClick={onCancel}>
        <Trans id="create-form-date-time-popover-cancel">Cancel</Trans>
      </Button>
      <Button variant={ButtonVariantsEnum.PRIMARY} size={VisualSizesEnum.SMALL} onClick={onDone}>
        <Trans id="create-form-date-time-popover-done">Done</Trans>
      </Button>
    </StyledButtonGroup>
  );
}

function renderDateTimeOrPlaceholderContent(
  value: string,
  placeholder?: string,
  customFormat?: Intl.DateTimeFormatOptions,
  shouldShowTimeZone?: boolean,
  timezone?: string,
) {
  if (value && isFinite(parseInt(value, 10))) {
    const dateTime = withTimezone(DateTime.fromMillis(Number(value)), timezone);

    if (!dateTime.isValid) {
      return null;
    }

    return (
      <StyledDateTimeTextSpan>
        <SmartDateTimeFormatter customFormat={customFormat} value={Number(value)} timezone={timezone} />
        {shouldShowTimeZone && customFormat?.dateStyle !== 'full' && ' '.concat(dateTime.offsetNameShort)}
      </StyledDateTimeTextSpan>
    );
  }

  if (placeholder) {
    return (
      <StyledPlaceholderDiv>
        <StyledPlaceholderTextSpan>{placeholder}</StyledPlaceholderTextSpan>
        <StyledDateTimeTextSpan>
          {shouldShowTimeZone && DateTime.local({zone: timezone}).offsetNameShort}
        </StyledDateTimeTextSpan>
      </StyledPlaceholderDiv>
    );
  }

  return (
    <StyledPlaceholderTextSpan>
      <Trans id="create-form-date-time-popover-select-date">Select date</Trans>
    </StyledPlaceholderTextSpan>
  );
}
